import { FC } from 'react'
import Swal from 'sweetalert2'
import {
  IconPaths,
  ModalSize,
  Permission,
  showAlertLoading,
  showConfirmAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { getEnderecoByIdPaciente, getPacienteById, updatePaciente } from '../../core/_requests'
import { useNavigate } from 'react-router-dom'
import { RowActions, useModal, useToast } from '../../../../../../components'
import { PacienteDtoUpdate, PacienteList } from '../../core/_models'
import { NewAtendimentoModal } from '../../../../atendimento/atendimento-list/components/modal/NewAtendimentoModal'
import { AgendarPacienteModal } from '../../../../agendamentos/components/AgendarPacienteModal'
import { PacienteModal } from '../../components/modal/PacienteModal'
import { useEsusCadastroPacienteItem } from '../../../../esus/cadastro/useEsusCadastroPacienteItem'
import { useAuth } from '../../../../../auth'

type Props = {
  paciente: PacienteList
}

export const PacienteActionsCell: FC<Props> = ({ paciente }) => {
  const { showModal } = useModal()
  const { showToast } = useToast()
  const { refetch } = useQueryResponse()
  const navigate = useNavigate()
  const { currentUser } = useAuth()

  const soCadastro = currentUser?.grupo?.nome === Permission.Solicitante
  const nome = paciente.pessoa?.nome || paciente.nome || 'Paciente'

  // Gate e diálogo do e-SUS moram no módulo e-SUS (ver `useEsusCadastroPacienteItem`).
  const esusCadastro = useEsusCadastroPacienteItem(paciente.id as number, paciente.nome)

  const showNovoAtendimento = () =>
    showModal({
      title: 'Novo Atendimento',
      content: <NewAtendimentoModal />,
      fetchers: {
        paciente: () => getPacienteById(paciente.id),
      },
    })

  /**
   * Agendar parte da ficha do paciente porque a empresa da reserva é a DELE:
   * `AgendamentoServiceImpl.reserva()` resolve a empresa por
   * `paciente.getEmpresa().getId()` e o `POST /agendamentos` é gateado por
   * `hasPermission(#dto.paciente, 'Paciente', 'Agendamento:create')`. Mesmo
   * `fetcher` do novo atendimento — o modal precisa do `PacienteView` completo.
   */
  const showAgendarHorario = () =>
    showModal({
      title: 'Agendar horário',
      content: <AgendarPacienteModal />,
      size: ModalSize.ExtraLarge,
      fetchers: {
        paciente: () => getPacienteById(paciente.id),
      },
    })

  const openEditModal = () => {
    showModal({
      title: 'Editar Paciente',
      content: <PacienteModal />,
      fetchers: {
        paciente: () => getPacienteById(paciente.id),
        endereco: async () => {
          const { data } = await getEnderecoByIdPaciente(paciente.id)
          return data[0]
        },
      },
    })
  }

  const openHistoricoClinico = () =>
    navigate(`/apps/atendimentosMedicos/historicoClinico/${paciente.id}`)

  const setStatus = async (ativo: boolean) => {
    try {
      showAlertLoading()
      const pac: PacienteDtoUpdate = { ativo }
      await updatePaciente(paciente.id as number, pac)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi ${ativo ? 'ativado' : 'inativado'}.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  // Inativar pede confirmação; ativar é reversível e não pede.
  const askInativar = async () => {
    const confirmed = await showConfirmAlert({
      title: 'Inativar paciente?',
      text: `${nome} ficará inativo. Você pode reativá-lo quando quiser.`,
      confirmButtonText: 'Inativar',
      cancelButtonText: 'Cancelar',
      danger: true,
    })
    if (confirmed) await setStatus(false)
  }

  const editar = { label: 'Editar', onClick: openEditModal, icon: IconPaths.Pen }
  return (
    <RowActions
      itemName={nome}
      primaryAction={
        soCadastro ? editar : { label: 'Novo atendimento', onClick: showNovoAtendimento }
      }
      items={[
        { label: 'Agendar', onClick: showAgendarHorario, hidden: soCadastro },
        { ...editar, hidden: soCadastro },
        { label: 'Histórico clínico', onClick: openHistoricoClinico, hidden: soCadastro },
        esusCadastro,
        paciente.ativo
          ? { label: 'Inativar', onClick: askInativar, danger: true, divider: true }
          : { label: 'Ativar', onClick: () => setStatus(true), divider: true },
      ]}
    />
  )
}
